import { useState, useEffect } from 'preact/hooks'
import { Building2 } from 'lucide-preact'
import type {
  ClientCompanyViewDto,
  WorkspaceCreateDto,
  WorkspaceUpdateDto
} from '@/types/company'
import { workspaceApi } from '@/api/workspace'
import { clientCompanyApi } from '@/api/clientCompany'
import { CompanyHeader } from './CompanyHeader'
import { CompanyEditModal } from './CompanyEditModal'
import { ConfirmationModal } from '../../modals/ConfirmationModal'
import { useToast } from '../../../hooks/useToast'
import { handleApiError } from '../../../utils/errorHandling'
import { useCompany } from '@/hooks/useCompany'

interface CompanyManagementProps {
  onNavigateHome?: () => void
  company?: ClientCompanyViewDto
  mode?: 'page' | 'embedded'
}

export function CompanyManagement({ company, mode = 'page' }: CompanyManagementProps) {
  const { updateCompany, companyId } = useCompany()
  const [selectedCompany, setSelectedCompany] = useState<ClientCompanyViewDto | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [newWorkspace, setNewWorkspace] = useState<WorkspaceCreateDto | null>(null)
  const [loading, setLoading] = useState(true)

  // Toast notifications
  const { showError, showSuccess } = useToast()
  // Company context mutators (kept in sync with TopNavigation)
  const { addWorkspace, updateWorkspace, removeWorkspace } = useCompany()

  // Confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [workspaceToDelete, setWorkspaceToDelete] = useState<{ id: string; name: string } | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Always fetch company data from API
  useEffect(() => {
    // If we have a company prop, use it
    if (company) {
      setSelectedCompany(company)
      setLoading(false)
      return
    }

    // Otherwise, fetch from API if we have a companyId
    if (companyId) {
      setLoading(true)
      clientCompanyApi.getById(companyId)
        .then(res => {
          if (res.data && !res.error) {
            setSelectedCompany(res.data)
            updateCompany(res.data)
          } else {
            handleApiError(res, showError, () => {}, 'Failed to load company')
          }
        })
        .catch((err) => {
          handleApiError(err, showError, () => {}, 'Failed to load company')
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [company, companyId])

  // Company editing is now fully handled via CompanyEditModal

  const handleAddWorkspace = () => {
    if (!selectedCompany) return
    setNewWorkspace({
      ClientCompanyId: selectedCompany.Id,
      Name: '',
      Slug: '',
      Description: '',
      Status: 'Active',
      TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      Locale: ''
    })
  }

  const handleSaveWorkspace = async () => {
    if (!newWorkspace || !selectedCompany || !selectedCompany.Id) {
      showError('Error', 'Missing required company information')
      return
    }

    setLoading(true)

    try {
      const response = await workspaceApi.create({
        ClientCompanyId: selectedCompany.Id,
        Name: newWorkspace.Name,
        Slug: newWorkspace.Slug,
        Description: newWorkspace.Description,
        Status: newWorkspace.Status,
        TimeZone: newWorkspace.TimeZone,
        Locale: newWorkspace.Locale,
        DailyLeadsSummaryEnabled: newWorkspace.DailyLeadsSummaryEnabled,
        DailyLeadsSummaryHour: newWorkspace.DailyLeadsSummaryHour,
      })

      if (response.error) {
        handleApiError(response, showError, () => {}, 'Failed to save workspace')
      } else {
        // Update the selected company with the new workspace
        const created = response.data!
        const updatedCompany = { ...selectedCompany, Workspaces: [...selectedCompany.Workspaces, created] }
        setSelectedCompany(updatedCompany)
        updateCompany(updatedCompany)
        addWorkspace(created)
        setNewWorkspace(null)
        showSuccess('Success', 'Workspace created successfully')
      }
    } catch (err) {
      handleApiError(err, showError, () => {}, 'Failed to save workspace')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteWorkspace = (workspaceId: string) => {
    if (!selectedCompany) return

    const workspace = selectedCompany.Workspaces.find(d => d.Id === workspaceId)
    if (!workspace) return

    // Check if this is the only workspace remaining
    if (selectedCompany.Workspaces.length === 1) {
      showError('Cannot Delete Workspace', 'Cannot delete the last remaining workspace. A company must have at least one workspace.')
      return
    }

    setWorkspaceToDelete({ id: workspaceId, name: workspace.Name })
    setShowDeleteConfirm(true)
  }

  const handleUpdateWorkspace = async (workspace: WorkspaceUpdateDto): Promise<boolean> => {
    if (!selectedCompany) return false

    setLoading(true)

    try {
      const response = await workspaceApi.update(workspace)
      if (response.error) {
        handleApiError(response, showError, () => {}, 'Failed to update workspace')
        return false
      } else {
        // Update the workspace in the selected company
        const updated = response.data!
        const updatedCompany = { ...selectedCompany, Workspaces: selectedCompany.Workspaces.map(d => (d.Id === workspace.Id ? updated : d)) }
        setSelectedCompany(updatedCompany)
        updateCompany(updatedCompany)
        updateWorkspace(updated)
        showSuccess('Success', 'Workspace updated successfully')
        return true
      }
    } catch (err) {
      handleApiError(err, showError, () => {}, 'Failed to update workspace')
      return false
    } finally {
      setLoading(false)
    }
  }

  const confirmDeleteWorkspace = async () => {
    if (!selectedCompany || !workspaceToDelete) return

    setDeleteLoading(true)

    try {
      const response = await workspaceApi.delete(selectedCompany.Id, workspaceToDelete.id)
      if (response.error) {
        handleApiError(response, showError, () => {}, 'Failed to delete workspace')
      } else {
        // Remove workspace from selected company
        const updatedCompany = { ...selectedCompany, Workspaces: selectedCompany.Workspaces.filter(d => d.Id !== workspaceToDelete.id) }
        setSelectedCompany(updatedCompany)
        updateCompany(updatedCompany)
        removeWorkspace(workspaceToDelete.id)
        setShowDeleteConfirm(false)
        setWorkspaceToDelete(null)
        showSuccess('Success', `Workspace "${workspaceToDelete.name}" deleted successfully`)
      }
    } catch (err) {
      handleApiError(err, showError, () => {}, 'Failed to delete workspace')
    } finally {
      setDeleteLoading(false)
    }
  }

  const cancelDeleteWorkspace = () => {
    setShowDeleteConfirm(false)
    setWorkspaceToDelete(null)
  }

  const content = (
    <>
      {loading && !selectedCompany && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Building2 className="h-12 w-12 text-slate-400 dark:text-slate-500 mx-auto mb-4 animate-pulse" />
            <div className="text-lg text-slate-600 dark:text-slate-300">Loading company...</div>
          </div>
        </div>
      )}

      {selectedCompany && (
        <CompanyHeader
          company={selectedCompany}
          onEditCompany={() => setIsEditModalOpen(true)}
          newWorkspace={newWorkspace}
          setNewWorkspace={setNewWorkspace}
          loading={loading}
          handleAddWorkspace={handleAddWorkspace}
          handleSaveWorkspace={handleSaveWorkspace}
          handleDeleteWorkspace={handleDeleteWorkspace}
          handleUpdateWorkspace={handleUpdateWorkspace}
        />
      )}

      {!loading && !selectedCompany && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Building2 className="h-12 w-12 text-slate-400 dark:text-slate-500 mx-auto mb-4" />
            <div className="text-slate-600 dark:text-slate-300">No company data available</div>
          </div>
        </div>
      )}
    </>
  )

  return (
    <div className={mode === 'page' ? 'min-h-screen bg-slate-50 dark:bg-slate-900' : ''}>
      <div className={mode === 'page' ? 'max-w-7xl mx-auto p-6 space-y-6' : ''}>
        {content}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Delete Workspace"
        message={`Are you sure you want to delete "${workspaceToDelete?.name}"? This action cannot be undone and will permanently remove the workspace and all its associated data.`}
        type="danger"
        confirmText="Delete Workspace"
        cancelText="Cancel"
        onConfirm={confirmDeleteWorkspace}
        onCancel={cancelDeleteWorkspace}
        loading={deleteLoading}
      />

      {/* Edit Company Modal */}
      {selectedCompany && (
        <CompanyEditModal
          isOpen={isEditModalOpen}
          company={selectedCompany}
          onClose={() => setIsEditModalOpen(false)}
          onSaved={(updated) => {
            setSelectedCompany(updated)
            updateCompany(updated)
          }}
        />
      )}
    </div>
  )
}
